import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { form, FormField } from '@angular/forms/signals';
import { MatFormField } from '@angular/material/form-field';
import { MatSelect, MatOption } from '@angular/material/select';

import { ORGANIZATION_FORM_SCHEMA } from './organization-form.schema-validators';
import { DEFAULT_ORGANIZATION_FORM_VALUE, ORGANIZATION_FORM_KIND } from './organization-form.types';
import { CreateEntityCommandService } from '../create-entity-command.service';
import { CreateEntityReferenceDataService } from '../create-entity-reference-data.service';
import { CreateEntityWizardStore } from '../create-entity-wizard.store';
import { COUNTRY_STATES, sortCountriesByPriority } from '../shared/country-states.constant';


@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-organization-form, wcg-organization-form',
    standalone: true,
    imports: [FormField, MatFormField, MatSelect, MatOption],
    templateUrl: './organization-form.component.html'
})
export class OrganizationFormComponent {
    readonly hidden = input.required<boolean>();

    private readonly _wizard = inject(CreateEntityWizardStore);
    private readonly _commands = inject(CreateEntityCommandService);
    private readonly _referenceData = inject(CreateEntityReferenceDataService);

    readonly $orgTypes = this._referenceData.$organizationTypes;

    /** Countries sorted with United States and Canada pinned to the top, mirroring the dashboard's `organization.facade.service.ts` `$sortedCountries`. */
    readonly $countries = computed(() => sortCountriesByPriority(this._referenceData.$countries()));

    readonly $referenceDataError = computed(() => this._referenceData.$organizationTypesError() || this._referenceData.$countriesError());

    /**
     * Options for the "Additional Organization Type" dropdown, mirroring the dashboard's
     * `CreateOrganizationDialogComponent.$additionalOrgTypes`: the currently selected primary
     * type is filtered out so the same org type can't be picked for both fields.
     */
    readonly $additionalOrgTypes = computed(() => {
        const _orgTypes = this.$orgTypes();
        const _primaryType = this.$form.primaryType().value();

        if (!_primaryType) {
            return _orgTypes;
        }

        return _orgTypes.filter(type => type.organization_type_key !== _primaryType);
    });

    /** Typed Signal Form, mirroring the dashboard's `CreateOrganizationDialogComponent.$organizationSignalForm` pattern. */
    readonly $model = signal({ ...DEFAULT_ORGANIZATION_FORM_VALUE });
    readonly $form = form(this.$model, ORGANIZATION_FORM_SCHEMA);

    /**
     * State/province options for the currently selected country, mirroring the dashboard's
     * `wcg-address` component: only United States and Canada have a curated list, so every other
     * country falls back to a free-text state/province input (see the template).
     */
    readonly $states = computed(() => COUNTRY_STATES.get(this.$form.country().value()) ?? []);

    constructor() {
        effect(() => {
            const _primaryType = this.$form.primaryType().value();
            untracked(() => {
                /*
                 * @NOTE:- Primary and Additional Org Types must stay mutually exclusive, mirroring
                 * the dashboard's CreateOrganizationDialogComponent. Every time the primary type
                 * changes, drop it from the additional-type selections if it's now duplicated there.
                 * */
                const _selectedKeys = this.$form.additionalType().value();
                if (_selectedKeys.includes(_primaryType)) {
                    this.$form.additionalType().controlValue.set(_selectedKeys.filter(key => key !== _primaryType));
                }
            });
        });

        effect(() => {
            const _country = this.$form.country().value();
            untracked(() => {
                /*
                 * @NOTE:- Mirrors the dashboard's `AddressComponent._stateManagementEffect`: whenever the
                 * user changes the country, the previously-entered state/province is stale (it may not
                 * even exist for the new country's dropdown), so reset it. The `touched()` guard skips
                 * this on initial mount.
                 * */
                if (_country && this.$form.country().touched()) {
                    this.$form.state().reset('');
                }
            });
        });

        /** "Save" was pressed while this tab was active — see `CreateEntityWizardStore` for why every `*FormComponent` reacts to its own tab's command instead of the parent holding a `viewChild` reference. */
        effect(() => {
            const _request = this._wizard.submitRequest();
            if (_request?.tab !== ORGANIZATION_FORM_KIND) {
                return;
            }

            untracked(() => this._trySubmit());
        });

        /** "+ Add Another" was pressed after this tab's entity was just created. */
        effect(() => {
            const _request = this._wizard.resetRequest();
            if (_request?.tab !== ORGANIZATION_FORM_KIND) {
                return;
            }

            untracked(() => this.reset());
        });
    }

    /** Triggered by the native `ngSubmit` when the user presses Enter inside the form; the "Save" button instead goes through `CreateEntityWizardStore.requestSubmit()`. */
    onSubmit(): void {
        this._trySubmit();
    }

    /** Resets the form back to its blank initial state (used by the "+ Add Another" flow). */
    reset(): void {
        this.$form().reset({ ...DEFAULT_ORGANIZATION_FORM_VALUE });
    }

    private _trySubmit(): void {
        if (this._wizard.submitting()) {
            return;
        }

        if (this.$form().invalid()) {
            this.$form().markAsTouched();
            return;
        }

        this._commands.createOrganization(this.$form().value());
    }
}
